// Typed wrapper around `import.meta.env`. Feature code reads from `config`
// exclusively — never `import.meta.env` directly. Add a new key here when a
// new VITE_* env var is introduced, then surface it on `config`.

export type AppEnv = 'development' | 'qa' | 'uat' | 'staging' | 'production' | 'test'
export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent'

interface RawEnv {
  MODE: string
  PROD: boolean
  DEV: boolean

  VITE_APP_ENV?: string
  VITE_LOG_LEVEL?: string

  VITE_AUTH_API_URL?: string
  VITE_INVESTOR_API_URL?: string

  VITE_SENTRY_DSN?: string
  VITE_SENTRY_ENVIRONMENT?: string
  VITE_SENTRY_RELEASE?: string
  VITE_SENTRY_TRACES_SAMPLE_RATE?: string

  VITE_ENABLE_WEB_VITALS?: string

  VITE_MARKETPLACE_FIXTURES?: string

  VITE_OTEL_OTLP_ENDPOINT?: string
  // OTel — optional per-signal overrides + tuning. When unset, the per-signal
  // URLs are derived from VITE_OTEL_OTLP_ENDPOINT by appending /v1/<signal>.
  VITE_OTEL_EXPORTER_OTLP_ENDPOINT?: string
  VITE_OTEL_EXPORTER_OTLP_TRACES_ENDPOINT?: string
  VITE_OTEL_EXPORTER_OTLP_METRICS_ENDPOINT?: string
  VITE_OTEL_EXPORTER_OTLP_LOGS_ENDPOINT?: string
  VITE_OTEL_SERVICE_NAME?: string
  VITE_OTEL_METRIC_EXPORT_INTERVAL_MS?: string
  VITE_OTEL_ENABLED?: string
}

const raw = import.meta.env as unknown as RawEnv

const PROD_TRACES_SAMPLE_RATE = 0.1
const NON_PROD_TRACES_SAMPLE_RATE = 1.0

const required = (name: keyof RawEnv): string => {
  const value = raw[name]
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`[config] missing required env var ${String(name)}`)
  }
  return value
}

const optional = (name: keyof RawEnv, fallback = ''): string => {
  const value = raw[name]
  return typeof value === 'string' && value.length > 0 ? value : fallback
}

const optionalFloat = (name: keyof RawEnv, fallback: number): number => {
  const value = raw[name]
  if (typeof value !== 'string' || value.length === 0) return fallback
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const optionalInt = (name: keyof RawEnv, fallback: number): number => {
  const value = raw[name]
  if (typeof value !== 'string' || value.length === 0) return fallback
  const parsed = Number.parseInt(value, 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

const optionalBool = (name: keyof RawEnv, fallback: boolean): boolean => {
  const value = raw[name]
  if (typeof value !== 'string' || value.length === 0) return fallback
  return value === 'true' || value === '1'
}

const appEnv: AppEnv = (() => {
  const envValue = optional('VITE_APP_ENV', raw.MODE)
  if (
    envValue === 'development' ||
    envValue === 'qa' ||
    envValue === 'uat' ||
    envValue === 'staging' ||
    envValue === 'production' ||
    envValue === 'test'
  ) {
    return envValue
  }
  return raw.PROD ? 'production' : 'development'
})()

const defaultLogLevel: LogLevel = (() => {
  switch (appEnv) {
    case 'production':
    case 'staging':
    case 'uat':
    case 'qa':
      return 'info'
    case 'test':
      return 'silent'
    default:
      return 'debug'
  }
})()

const parseLogLevel = (raw: string | undefined, fallback: LogLevel): LogLevel => {
  if (!raw) return fallback
  if (raw === 'debug' || raw === 'info' || raw === 'warn' || raw === 'error' || raw === 'silent') {
    return raw
  }
  return fallback
}

// Kibana "Executive" analytics dashboard — surfaced as an external link on the
// admin dashboard. The URL differs per environment; an empty string hides the
// link for that environment. Kept in code (per-env map, not an env var) so a
// deploy needs no extra wiring — add the URL to the matching slot below.
const KIBANA_EXECUTIVE_DASHBOARD_URL: Record<AppEnv, string> = {
  development: 'https://kibana-dev.sliceal.io/executive-dev/', // inferred from the UAT pattern — verify
  qa: 'https://kibana-dev.sliceal.io/executive-qa/', // inferred from the UAT pattern — verify
  uat: 'https://kibana-dev.sliceal.io/executive-uat/',
  staging: '',
  production: '',
  test: '',
}

// Kibana APM (service map) — surfaced as an external link in the monitoring nav.
// Per-env `environment=` query param scopes the view; empty string hides the link.
const KIBANA_APM_URL: Record<AppEnv, string> = {
  development:
    'https://kibana-dev.sliceal.io/app/apm/services?comparisonEnabled=true&environment=development&rangeFrom=now-15m&rangeTo=now&offset=1d',
  qa: 'https://kibana-dev.sliceal.io/app/apm/services?comparisonEnabled=true&environment=qa&rangeFrom=now-15m&rangeTo=now&offset=1d',
  uat: 'https://kibana-dev.sliceal.io/app/apm/services?comparisonEnabled=true&environment=uat&rangeFrom=now-15m&rangeTo=now&offset=1d',
  staging: '',
  production: '',
  test: '',
}

// Kibana Discover (backend logs) — surfaced as an external link in the monitoring
// nav. Per-env `dataViewId` targets that environment's log index; empty string
// hides the link.
const KIBANA_LOGS_URL: Record<AppEnv, string> = {
  development:
    "https://kibana-dev.sliceal.io/app/discover#/?_g=(time:(from:now-1h,to:now))&_a=(columns:!(),dataSource:(dataViewId:dev-backend-dv,type:dataView),filters:!(),interval:auto,query:(language:kuery,query:''),sort:!(!(timestamp,desc)))",
  qa: 'https://kibana-dev.sliceal.io/app/discover#/?_g=(time:(from:now-1h,to:now))&_a=(dataSource:(dataViewId:qa-backend-dv,type:dataView),sort:!(!(timestamp,desc)))',
  uat: 'https://kibana-dev.sliceal.io/app/discover#/?_g=(time:(from:now-1h,to:now))&_a=(dataSource:(dataViewId:a687033a-da51-4321-83cb-2ff430803878,type:dataView),sort:!(!(timestamp,desc)))',
  staging: '',
  production: '',
  test: '',
}

export const config = {
  appEnv,
  isProd: appEnv === 'production',
  isDev: appEnv === 'development',
  isQa: appEnv === 'qa',
  isUat: appEnv === 'uat',
  isStaging: appEnv === 'staging',
  isTest: appEnv === 'test',

  app: {
    name: __APP_NAME__,
    version: __APP_VERSION__,
  },

  logLevel: parseLogLevel(optional('VITE_LOG_LEVEL'), defaultLogLevel),

  authApiUrl: required('VITE_AUTH_API_URL'),
  adminApiUrl: required('VITE_INVESTOR_API_URL'),

  monitoring: {
    // Resolved for the current environment; empty string when not configured.
    kibanaExecutiveDashboardUrl: KIBANA_EXECUTIVE_DASHBOARD_URL[appEnv],
    kibanaApmUrl: KIBANA_APM_URL[appEnv],
    kibanaLogsUrl: KIBANA_LOGS_URL[appEnv],
  },

  sentry: {
    dsn: optional('VITE_SENTRY_DSN'),
    environment: optional('VITE_SENTRY_ENVIRONMENT', appEnv),
    release: optional('VITE_SENTRY_RELEASE'),
    tracesSampleRate: optionalFloat(
      'VITE_SENTRY_TRACES_SAMPLE_RATE',
      appEnv === 'production' ? PROD_TRACES_SAMPLE_RATE : NON_PROD_TRACES_SAMPLE_RATE,
    ),
  },

  marketplace: {
    // Serve typed sample data from the marketplace module instead of calling
    // /api/admin/marketplace, which admin-service doesn't expose yet. Off unless
    // a developer opts in through their local .env. Remove with the flag once
    // the backend lands.
    useFixtures: optionalBool('VITE_MARKETPLACE_FIXTURES', false),
  },

  webVitals: {
    // Default: on in staging/production, off in local dev to keep the console quiet.
    enabled: optionalBool('VITE_ENABLE_WEB_VITALS', appEnv !== 'development'),
  },

  otel: (() => {
    const DEFAULT_METRIC_EXPORT_INTERVAL_MS = 60_000
    // Browser-facing OTLP collector endpoint. Leave blank to disable OTel.
    // Two env names are accepted for back-compat: VITE_OTEL_OTLP_ENDPOINT
    // (original) and VITE_OTEL_EXPORTER_OTLP_ENDPOINT (matches the backend
    // services' env naming). The latter wins when both are set.
    const otlpEndpoint =
      optional('VITE_OTEL_EXPORTER_OTLP_ENDPOINT') || optional('VITE_OTEL_OTLP_ENDPOINT')

    const trimmedBase = otlpEndpoint.replace(/\/$/, '')

    // Per-signal override → otherwise derive from the base endpoint.
    const tracesUrl =
      optional('VITE_OTEL_EXPORTER_OTLP_TRACES_ENDPOINT') ||
      (trimmedBase ? `${trimmedBase}/v1/traces` : '')
    const metricsUrl =
      optional('VITE_OTEL_EXPORTER_OTLP_METRICS_ENDPOINT') ||
      (trimmedBase ? `${trimmedBase}/v1/metrics` : '')
    const logsUrl =
      optional('VITE_OTEL_EXPORTER_OTLP_LOGS_ENDPOINT') ||
      (trimmedBase ? `${trimmedBase}/v1/logs` : '')

    return {
      // Default to true when an endpoint is configured; set
      // VITE_OTEL_ENABLED=false to disable without unsetting the URL.
      enabled: optionalBool('VITE_OTEL_ENABLED', otlpEndpoint.length > 0),
      otlpEndpoint,
      tracesUrl,
      metricsUrl,
      logsUrl,
      serviceName: optional('VITE_OTEL_SERVICE_NAME', __APP_NAME__),
      metricExportIntervalMs: optionalInt(
        'VITE_OTEL_METRIC_EXPORT_INTERVAL_MS',
        DEFAULT_METRIC_EXPORT_INTERVAL_MS,
      ),
    }
  })(),
} as const

export type Config = typeof config
